import clsx from 'clsx';
import { useEffect, useMemo, useReducer, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronRight,
  Loader2,
  RotateCcw,
  X as XIcon
} from 'lucide-react';
import { Button } from '../../components/Button';
import { LoadingState, ErrorState } from '../../components/LoadingState';
import { useAppParams } from '../../api/params';
import { useEvaluateShortAnswer, useGenerateQuiz } from '../../api/queries';
import { defaultDraft, hintFor, type Draft, type Question } from './types';
import { isComplete, isCorrect } from './scoring';
import { MultipleChoice } from './questions/MultipleChoice';
import { TrueFalse } from './questions/TrueFalse';
import { Ordering } from './questions/Ordering';
import { FillBlank } from './questions/FillBlank';
import { Grouping } from './questions/Grouping';
import { Matching } from './questions/Matching';
import { OpenEnded, type Assessment } from './questions/OpenEnded';
import styles from './QuizView.module.css';

// ── State machine ───────────────────────────────────────────────────────────

type State = {
  step: number;
  drafts: Draft[];
  submitted: boolean[];
  correct: boolean[];
  // for open_ended only
  assessments: (Assessment | undefined)[];
};

type Action =
  | { type: 'INIT'; questions: Question[] }
  | { type: 'SET_DRAFT'; step: number; draft: Draft }
  | { type: 'MARK'; step: number; correct: boolean; assessment?: Assessment }
  | { type: 'NEXT' }
  | { type: 'PREV' }
  | { type: 'RESET'; questions: Question[] };

const init = (questions: Question[]): State => ({
  step: 0,
  drafts: questions.map((q, i) => defaultDraft(q, i + 1)),
  submitted: questions.map(() => false),
  correct: questions.map(() => false),
  assessments: questions.map(() => undefined)
});

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'INIT':
    case 'RESET':
      return init(action.questions);
    case 'SET_DRAFT': {
      const drafts = state.drafts.slice();
      drafts[action.step] = action.draft;
      return { ...state, drafts };
    }
    case 'MARK': {
      const submitted = state.submitted.slice();
      const correct = state.correct.slice();
      const assessments = state.assessments.slice();
      submitted[action.step] = true;
      correct[action.step] = action.correct;
      assessments[action.step] = action.assessment;
      return { ...state, submitted, correct, assessments };
    }
    case 'NEXT':
      return { ...state, step: Math.min(state.step + 1, state.drafts.length) };
    case 'PREV':
      return { ...state, step: Math.max(state.step - 1, 0) };
  }
}

// ── Helpers ─────────────────────────────────────────────────────────────────

// fill_blank questions occasionally come back malformed (no {n} markers in
// `sentence`). The legacy filter does the same — drop them.
function sanitize(qs: any[]): Question[] {
  return qs.filter((q) => {
    if (q?.format === 'fill_blank') {
      return /\{\d+\}/.test(q.sentence ?? '');
    }
    return !!q?.format;
  }) as Question[];
}

// ── Component ───────────────────────────────────────────────────────────────

export function QuizView() {
  const params = useAppParams();
  const mut = useGenerateQuiz();
  const evaluator = useEvaluateShortAnswer();
  const [state, dispatch] = useReducer(reducer, [], () => init([]));
  const [pending, setPending] = useState(false);
  // When finished, clicking a row in the summary opens its detail view.
  const [reviewStep, setReviewStep] = useState<number | null>(null);
  const count = params?.count ?? 5;

  useEffect(() => {
    if (!mut.data && !mut.isPending && !mut.error) mut.mutate({ count });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const questions = useMemo<Question[]>(() => {
    const raw = (mut.data as any)?.content?.questions ?? [];
    return sanitize(raw);
  }, [mut.data]);

  // Initialise state once we have the questions.
  useEffect(() => {
    if (questions.length > 0 && state.drafts.length === 0) {
      dispatch({ type: 'INIT', questions });
    }
  }, [questions, state.drafts.length]);

  if (mut.isPending) return <LoadingState label="Preparando preguntas…" />;
  if (mut.error) return <ErrorState error={mut.error} onRetry={() => mut.mutate({ count })} />;
  if (questions.length === 0) return null;
  if (state.drafts.length === 0) return null;

  const total = questions.length;
  const finished = state.step >= total;

  // ── Results screen ────────────────────────────────────────────────────────
  if (finished) {
    const correct = state.correct.filter(Boolean).length;
    const pct = Math.round((correct / total) * 100);

    // Detail view: render the chosen question in review mode.
    if (reviewStep !== null && reviewStep < total) {
      const rq = questions[reviewStep];
      const rDraft = state.drafts[reviewStep];
      const rCorrect = state.correct[reviewStep];
      const rAssessment = state.assessments[reviewStep];
      return (
        <div className={styles.view}>
          <div className={styles.progress}>
            <button
              type="button"
              onClick={() => setReviewStep(null)}
              className={styles.backBtn}
              aria-label="Volver a resultados"
            >
              <ArrowLeft size={16} />
            </button>
            <span className={styles.progressLabel}>
              Pregunta {reviewStep + 1} / {total}
            </span>
            <span
              className={clsx(
                styles.reviewBadge,
                rCorrect ? styles.reviewBadgeOk : styles.reviewBadgeKo
              )}
            >
              {rCorrect ? <Check size={14} /> : <XIcon size={14} />}
            </span>
          </div>

          <div className={styles.scrollBody}>
            {rq.question && <h3 className={styles.question}>{rq.question}</h3>}
            {!rq.instruction && (
              <p className={styles.hint}>{hintFor(rq.format)}</p>
            )}
            <QuestionBody
              q={rq}
              draft={rDraft}
              onChange={() => {}}
              disabled
              showFeedback
              assessment={rAssessment}
              pending={false}
            />
            {rq.explanation && (
              <div className={styles.explanationBox}>
                <span className={styles.explanationLabel}>Explicación</span>
                <p>{rq.explanation}</p>
              </div>
            )}
          </div>

          <div className={styles.footer}>
            <Button onClick={() => setReviewStep(null)} variant="primary" fullWidth>
              Volver al resumen
            </Button>
          </div>
        </div>
      );
    }

    // Summary view: gauge + clickable list of questions.
    return (
      <div className={styles.summary}>
        <ScoreGauge pct={pct} correct={correct} total={total} />
        <h3 className={styles.summaryTitle}>
          {pct >= 70 ? '¡Dominas el tema!' : 'Sigue practicando'}
        </h3>
        <p className={styles.summaryBody}>
          Has acertado <strong>{correct}</strong> de <strong>{total}</strong> ({pct}%).
        </p>

        <ol className={styles.reviewList}>
          {questions.map((rq, i) => (
            <li key={i}>
              <button
                type="button"
                className={styles.reviewRow}
                onClick={() => setReviewStep(i)}
              >
                <span
                  className={clsx(
                    styles.reviewDot,
                    state.correct[i] ? styles.reviewDotOk : styles.reviewDotKo
                  )}
                  aria-hidden
                >
                  {state.correct[i] ? <Check size={12} /> : <XIcon size={12} />}
                </span>
                <span className={styles.reviewRowText}>
                  {(rq.question ?? rq.instruction ?? `Pregunta ${i + 1}`).slice(0, 90)}
                  {(rq.question ?? rq.instruction ?? '').length > 90 ? '…' : ''}
                </span>
                <ChevronRight size={16} className={styles.reviewChev} />
              </button>
            </li>
          ))}
        </ol>

        <Button
          onClick={() => {
            setReviewStep(null);
            dispatch({ type: 'RESET', questions });
          }}
          variant="primary"
        >
          <RotateCcw size={16} strokeWidth={2.4} /> Repetir
        </Button>
      </div>
    );
  }

  const q = questions[state.step];
  const draft = state.drafts[state.step];
  const submitted = state.submitted[state.step];
  const wasCorrect = state.correct[state.step];
  const assessment = state.assessments[state.step];

  const setDraft = (d: Draft) =>
    dispatch({ type: 'SET_DRAFT', step: state.step, draft: d });

  const submit = async () => {
    if (submitted) return;
    if (q.format === 'open_ended') {
      const d = draft as Extract<Draft, { format: 'open_ended' }>;
      const modelAnswer = q.correct_answer ?? q.answer ?? '';
      if (!modelAnswer) {
        // Without a model answer the backend rejects the request — fall back
        // to a non-judging "submitted" state so the user can move on.
        dispatch({
          type: 'MARK',
          step: state.step,
          correct: false,
          assessment: { score: 'needs_improvement' }
        });
        return;
      }
      setPending(true);
      try {
        const res = (await evaluator.mutateAsync({
          question: q.question ?? '',
          model_answer: modelAnswer,
          student_response: d.text
        })) as any;
        const a: Assessment = res?.content?.assessment ?? {};
        const ok = a.score === 'excellent' || a.score === 'good';
        dispatch({ type: 'MARK', step: state.step, correct: ok, assessment: a });
      } catch {
        dispatch({
          type: 'MARK',
          step: state.step,
          correct: false,
          assessment: { score: 'needs_improvement' }
        });
      } finally {
        setPending(false);
      }
      return;
    }
    const ok = isCorrect(q, draft);
    dispatch({ type: 'MARK', step: state.step, correct: ok });
  };

  const advance = () => dispatch({ type: 'NEXT' });
  const back = () => dispatch({ type: 'PREV' });

  const ready = isComplete(q, draft);
  const showFeedback = submitted;
  const disabled = submitted;

  return (
    <div className={styles.view}>
      <div className={styles.progress}>
        <button
          type="button"
          onClick={back}
          disabled={state.step === 0}
          className={styles.backBtn}
          aria-label="Pregunta anterior"
        >
          <ArrowLeft size={16} />
        </button>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${(state.step / total) * 100}%` }} />
        </div>
        <span className={styles.progressLabel}>
          {state.step + 1} / {total}
        </span>
      </div>

      <div className={styles.scrollBody}>
        {q.question && <h3 className={styles.question}>{q.question}</h3>}
        {!q.instruction && (
          <p className={styles.hint}>{hintFor(q.format)}</p>
        )}

        <QuestionBody
          q={q}
          draft={draft}
          onChange={setDraft}
          disabled={disabled}
          showFeedback={showFeedback}
          assessment={assessment}
          pending={pending}
        />

        {showFeedback && (
          <div className={clsx(styles.feedback, wasCorrect ? styles.feedbackOk : styles.feedbackKo)}>
            <strong>{wasCorrect ? '¡Correcto!' : 'No del todo.'}</strong>
            {q.explanation && <p>{q.explanation}</p>}
          </div>
        )}
      </div>

      <div className={styles.footer}>
        {!submitted ? (
          <Button
            onClick={submit}
            variant="primary"
            disabled={!ready || pending}
            fullWidth
          >
            {pending ? (
              <>
                <Loader2 size={16} className={styles.spin} /> Evaluando…
              </>
            ) : (
              'Comprobar'
            )}
          </Button>
        ) : (
          <Button onClick={advance} variant="primary" fullWidth>
            {state.step + 1 === total ? 'Ver resultados' : <>Siguiente <ArrowRight size={16} /></>}
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

type BodyProps = {
  q: Question;
  draft: Draft;
  onChange: (d: Draft) => void;
  disabled: boolean;
  showFeedback: boolean;
  assessment?: Assessment;
  pending: boolean;
};

function QuestionBody({ q, draft, onChange, disabled, showFeedback, assessment, pending }: BodyProps) {
  switch (q.format) {
    case 'options':
      return <MultipleChoice question={q} draft={draft as any} onChange={onChange} disabled={disabled} showFeedback={showFeedback} />;
    case 'true_false':
      return <TrueFalse question={q} draft={draft as any} onChange={onChange} disabled={disabled} showFeedback={showFeedback} />;
    case 'element_order':
      return <Ordering question={q} draft={draft as any} onChange={onChange} disabled={disabled} showFeedback={showFeedback} />;
    case 'fill_blank':
      return <FillBlank question={q} draft={draft as any} onChange={onChange} disabled={disabled} showFeedback={showFeedback} />;
    case 'grouping':
      return <Grouping question={q} draft={draft as any} onChange={onChange} disabled={disabled} showFeedback={showFeedback} />;
    case 'matching':
      return <Matching question={q} draft={draft as any} onChange={onChange} disabled={disabled} showFeedback={showFeedback} />;
    case 'open_ended':
      return (
        <OpenEnded
          question={q}
          draft={draft as any}
          onChange={onChange}
          disabled={disabled || pending}
          showFeedback={showFeedback}
          assessment={assessment}
          modelAnswer={q.correct_answer ?? q.answer}
        />
      );
  }
}

// Circular SVG gauge — inspired by Study's QuizSummaryPage. Colours by
// threshold so the user gets an immediate read of how they did.
function ScoreGauge({ pct, correct, total }: { pct: number; correct: number; total: number }) {
  const size = 120;
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  const color =
    pct >= 80 ? 'var(--color-success)' : pct >= 60 ? 'var(--color-warning)' : 'var(--color-danger)';
  return (
    <div className={styles.gauge} style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="var(--color-border)"
          strokeWidth={stroke}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={color}
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          className={styles.gaugeArc}
        />
      </svg>
      <div className={styles.gaugeText}>
        <span className={styles.gaugeNum}>
          {correct}
          <span className={styles.gaugeTotal}>/{total}</span>
        </span>
        <span className={styles.gaugePct}>{pct}%</span>
      </div>
    </div>
  );
}
